import { z } from 'zod';
import { AccessLevel, AuditMeta, ContentRing, DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Firestore path: documents/{docId}
 *
 * Stores metadata + version history; raw bytes live in Cloud Storage,
 * embedded chunks live in Vertex AI Vector Search.
 */
export const DocumentSchema = AuditMeta.extend({
    id: DocId,
    /**
     * Tenant that owns the document. Ring 1 documents are tenant-scoped.
     * Ring 2 documents use `_platform` as a pseudo-tenant (cross-tenant readable).
     */
    tenantId: z.union([TenantId, z.literal('_platform')]),
    title: z.string().min(1).max(500),
    ring: ContentRing,
    accessLevel: AccessLevel,
    /**
     * Category ID (FK into categories/). Null for Ring 2 imports that don't
     * map cleanly into the SUPPOHRT taxonomy.
     */
    categoryId: DocId.nullable(),
    sourceType: z.enum(['pdf', 'docx', 'xlsx', 'external_url']),
    sourceUri: z.string(),
    /**
     * Monotonic per-document. Bumped when a new version is uploaded.
     */
    version: z.string().min(1).max(32),
    /**
     * SHA-256 of the raw source bytes. Used for idempotent re-ingestion.
     */
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    /**
     * Ring 2 external sources carry origin metadata for citation.
     */
    origin: z
        .object({
        url: z.string().url(),
        provider: z.string().min(1),
        lastCrawledAt: IsoTimestamp.optional(),
    })
        .optional(),
    indexStatus: z.enum(['pending', 'extracting', 'chunking', 'embedding', 'indexed', 'failed']),
    indexError: z.string().optional(),
    chunkCount: z.number().int().nonnegative().optional(),
    lastVerifiedAt: IsoTimestamp.optional(),
});
/**
 * Input for upload-endpoint payloads. Server computes ID, hash, index status.
 */
export const CreateDocumentInput = z.object({
    title: z.string().min(1).max(500),
    ring: ContentRing,
    accessLevel: AccessLevel,
    categoryId: DocId.nullable(),
    sourceType: DocumentSchema.shape.sourceType,
    sourceUri: DocumentSchema.shape.sourceUri,
    version: z.string().min(1).max(32).default('v1'),
});
//# sourceMappingURL=document.js.map